'use client';

import { Button } from '~/components/ui/button';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '../ui/form';
import { Input } from '~/components/ui/input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormInput } from '../forms/form-input';
import { FormTextArea } from '../forms/form-textarea';
import {
  type OverprintingFormWithFileSchema,
  overprintingFormWithFileSchema,
} from '~/validation-schemas/overprinting-form';
import { toast } from 'sonner';
import { cn } from '~/lib/utils';
import { type CallToActionButtonStyleReturn } from './call-to-action';
import { useRef } from 'react';
import { submitOverprintingFormWithFile } from '~/actions/overprinting/overprinting-actions';

interface BlockOverprintingFormProps {
  className?: string;
  customButton: CallToActionButtonStyleReturn;
  formIdentifier: string;
  pageUrl: string;
}

export function BlockOverprintingForm({
  className,
  customButton,
  formIdentifier,
  pageUrl,
}: Readonly<BlockOverprintingFormProps>) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const form = useForm<OverprintingFormWithFileSchema>({
    resolver: zodResolver(overprintingFormWithFileSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      schoolName: '',
      notes: '',
      overprintingFile: undefined,
    },
  });

  const onSubmit = async (data: OverprintingFormWithFileSchema) => {
    try {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('email', data.email);
      formData.append('phone', data.phone);
      formData.append('schoolName', data.schoolName);
      formData.append('notes', data.notes);
      formData.append('formIdentifier', formIdentifier);
      formData.append('pageUrl', pageUrl);
      formData.append('overprintingFile', data.overprintingFile);

      toast.loading('Submitting overprinting form...', {
        id: 'overprinting-form',
      });

      const result = await submitOverprintingFormWithFile(null, formData);

      if (!result.success) {
        toast.error(result.message, { id: 'overprinting-form' });
        return;
      }

      toast.success(result.message || 'Form submitted successfully', {
        id: 'overprinting-form',
      });
      form.reset();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('Error submitting overprinting form:', error);
      toast.error('Form submission failed', { id: 'overprinting-form' });
    }
  };

  const isDisabled = form.formState.isSubmitting;

  return (
    <div className={cn('w-full sm:mx-auto sm:max-w-3xl', className)}>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 gap-4 px-2 sm:grid-cols-2">
            <div className="col-span-1">
              <FormInput
                id="name"
                name="name"
                label="Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your name"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="email"
                name="email"
                label="Email Address"
                type="email"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your email address"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="phone"
                name="phone"
                label="Phone Number"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your phone number"
              />
            </div>
            <div className="col-span-1">
              <FormInput
                id="schoolName"
                name="schoolName"
                label="School Name"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your school name"
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <FormTextArea
                id="notes"
                name="notes"
                label="Notes"
                control={form.control}
                disabled={isDisabled}
                placeholder="Enter your overprinting requirements"
                rows={4}
              />
            </div>
            <div className="col-span-1 sm:col-span-2">
              <FormField
                control={form.control}
                name="overprintingFile"
                render={({ field: { onChange } }) => (
                  <FormItem>
                    <FormLabel>Upload File</FormLabel>
                    <FormControl>
                      <Input
                        ref={fileInputRef}
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png,.tiff,.tif"
                        disabled={isDisabled}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          onChange(file);
                        }}
                      />
                    </FormControl>
                    <p className="text-xs text-muted-foreground">
                      Upload files related to your overprinting requirements
                      (PDF, JPG, PNG, TIFF)
                    </p>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="col-span-1 mt-2 sm:col-span-2">
              <Button
                type="submit"
                disabled={isDisabled}
                loader={isDisabled}
                style={customButton?.customButtonStyle}
                className={cn(
                  'mx-auto block w-full sm:w-1/2',
                  customButton?.customButtonClasses,
                )}
                variant={customButton ? 'custom' : 'default'}
              >
                Submit Overprinting Request
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
